import { useEffect, useState } from "react";
import { Copy, Gift, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice } from "@/lib/api";

const btn = "inline-flex items-center gap-2 rounded-full border border-amber-800/25 px-4 py-2 text-sm font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep";

function Rules({ s }) {
  return (
    <p data-testid="referral-rules" className="mt-1 text-sm text-bark-soft">
      Partagez votre lien : vos proches qui créent leur compte grâce à lui profitent de −{s.welcome_discount_percent} % sur leur première commande, et vous gagnez{" "}
      {s.first_account_percent} % de leurs points à leur 1<sup>re</sup> commande, puis {s.next_account_percent} % sur toutes les suivantes.
    </p>
  );
}

export default function ReferralShare() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/customer/referral").then(({ data: d }) => setData(d)).catch(() => setData(null));
  }, []);

  if (!data || !data.settings.enabled) return null;
  const link = `${window.location.origin}/?parrain=${data.code}`;
  const text = `Je te fais découvrir Pétales de Miel, des miels artisanaux délicieux ! En créant ton compte avec mon lien, tu as −${data.settings.welcome_discount_percent} % sur ta première commande : ${link}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Lien copié !");
    } catch {
      toast.error("Copie impossible, sélectionnez le lien à la main");
    }
  };

  return (
    <div data-testid="referral-share" className="rounded-2xl border border-amber-800/15 bg-white p-8 shadow-sm">
      <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-bark"><Gift size={20} className="text-amber-deep" /> Parrainez vos proches</h2>
      <Rules s={data.settings} />
      <p data-testid="referral-link" className="mt-4 break-all rounded-xl bg-amber-50/70 px-4 py-3 font-mono text-sm text-bark">{link}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button data-testid="referral-copy" onClick={copy} className={btn}><Copy size={15} /> Copier le lien</button>
        <a data-testid="referral-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" className={btn}><MessageCircle size={15} /> WhatsApp</a>
        <a data-testid="referral-email" href={`mailto:?subject=${encodeURIComponent("Un cadeau pour toi : −" + data.settings.welcome_discount_percent + " % chez Pétales de Miel")}&body=${encodeURIComponent(text)}`} className={btn}><Mail size={15} /> E-mail</a>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl bg-amber-50/70 px-4 py-3"><p data-testid="referral-count" className="font-serif text-2xl font-semibold text-bark">{data.relatives_count}</p><p className="text-xs text-bark-soft">proche{data.relatives_count > 1 ? "s" : ""} parrainé{data.relatives_count > 1 ? "s" : ""}</p></div>
        <div className="rounded-xl bg-amber-50/70 px-4 py-3"><p data-testid="referral-total" className="font-serif text-2xl font-semibold text-bark">{formatPrice(data.total_cents)}</p><p className="text-xs text-bark-soft">commandés grâce à vous</p></div>
        <div className="rounded-xl bg-amber-50/70 px-4 py-3"><p data-testid="referral-points" className="font-serif text-2xl font-semibold text-bark">+{data.points}</p><p className="text-xs text-bark-soft">points gagnés</p></div>
      </div>
      {data.relatives.length > 0 && (
        <ul className="mt-5 divide-y divide-amber-900/10">
          {data.relatives.map((r, i) => (
            <li key={i} data-testid={`referral-relative-${i}`} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
              <div>
                <p className="font-medium text-bark">{r.name}</p>
                <p className="text-xs text-stone-mute">{r.orders} commande{r.orders > 1 ? "s" : ""} · {formatPrice(r.total_cents)}{r.has_account ? " · a un compte" : ""}</p>
              </div>
              <span className="rounded-full bg-sage-light px-3 py-1 text-xs font-semibold text-sage">+{r.points} points</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
